interface PostDraft {
  id: string;
  title: string;
  description: string;
  selectedCategory: string;
  pollQuestion: string;
  pollOptions: Array<{ id: string; text: string }>;
  showPoll: boolean;
  images?: string[];
  video?: string;
  createdAt: string;
  updatedAt: string;
}

class DraftService {
  private readonly API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3009';

  private async getAuthToken(): Promise<string | null> {
    try {
      // Try environment variable first (for development)
      const envToken = import.meta.env.VITE_JWT_TOKEN;
      if (envToken) return envToken;

      // Try localStorage
      const token = localStorage.getItem('chime_token');
      return token;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}) {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    };

    const response = await fetch(`${this.API_BASE_URL}${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      // Don't log 404 errors for autosave endpoint (expected when no draft exists)
      if (!(response.status === 404 && url.includes('/autosave'))) {
        console.error(`Draft API Error: ${response.status} ${response.statusText}`, errorText);
      }
      throw new Error(`API request failed: ${response.status} ${errorText}`);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  async saveDraft(draft: Omit<PostDraft, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const createdDraft = await this.fetchWithAuth('/api/v1/drafts', {
        method: 'POST',
        body: JSON.stringify({
          ...draft,
          isAutoSave: false
        }),
      });
      return createdDraft.id;
    } catch (error) {
      console.error('Failed to save draft:', error);
      throw error;
    }
  }

  async updateDraft(id: string, updates: Partial<Omit<PostDraft, 'id' | 'createdAt'>>): Promise<void> {
    try {
      await this.fetchWithAuth(`/api/v1/drafts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
    } catch (error) {
      console.error('Failed to update draft:', error);
      throw error;
    }
  }

  async getDrafts(): Promise<PostDraft[]> {
    try {
      const drafts = await this.fetchWithAuth('/api/v1/drafts');
      return drafts || [];
    } catch (error) {
      console.error('Failed to load drafts:', error);
      return [];
    }
  }

  async getDraft(id: string): Promise<PostDraft | null> {
    try {
      const draft = await this.fetchWithAuth(`/api/v1/drafts/${id}`);
      return draft;
    } catch (error) {
      console.error('Failed to load draft:', error);
      return null;
    }
  }

  async deleteDraft(id: string): Promise<void> {
    try {
      await this.fetchWithAuth(`/api/v1/drafts/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete draft:', error);
      throw error;
    }
  }

  async deleteAllDrafts(): Promise<void> {
    try {
      await this.fetchWithAuth('/api/v1/drafts', {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete all drafts:', error);
      throw error;
    }
  }

  // Auto-save current draft
  async autoSave(draft: Omit<PostDraft, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const createdDraft = await this.fetchWithAuth('/api/v1/drafts', {
        method: 'POST',
        body: JSON.stringify({
          ...draft,
          isAutoSave: true
        }),
      });
      return createdDraft.id;
    } catch (error) {
      console.error('Failed to auto-save draft:', error);
      throw error;
    }
  }

  async getAutoSave(): Promise<PostDraft | null> {
    try {
      const draft = await this.fetchWithAuth('/api/v1/drafts/autosave');
      return draft;
    } catch (error) {
      // 404 is expected when there's no auto-save
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      console.error('Failed to load auto-save:', error);
      return null;
    }
  }

  async clearAutoSave(): Promise<void> {
    try {
      await this.fetchWithAuth('/api/v1/drafts/autosave', {
        method: 'DELETE',
      });
    } catch (error) {
      // 404 is okay - means there's no auto-save to clear
      if (error instanceof Error && error.message.includes('404')) {
        return;
      }
      console.error('Failed to clear auto-save:', error);
      throw error;
    }
  }
}

export const draftService = new DraftService();
export type { PostDraft };
